// ── Estatísticas descritivas ──────────────────────────────────────────────
export function descritiva(valores) {
  const v = valores.filter(x => x !== null && x !== undefined && x !== '' && !isNaN(Number(x))).map(Number);
  if (!v.length) return null;
  const n = v.length;
  const media = v.reduce((a, b) => a + b, 0) / n;
  const sorted = [...v].sort((a, b) => a - b);
  const mediana = n % 2 === 0 ? (sorted[n/2-1] + sorted[n/2]) / 2 : sorted[Math.floor(n/2)];
  const variancia = v.reduce((a, b) => a + Math.pow(b - media, 2), 0) / (n - 1);
  const dp = Math.sqrt(variancia);
  return {
    n, media: +media.toFixed(2), mediana: +mediana.toFixed(2),
    dp: +dp.toFixed(2), min: +Math.min(...v).toFixed(2), max: +Math.max(...v).toFixed(2),
    q1: +sorted[Math.floor(n * 0.25)].toFixed(2), q3: +sorted[Math.floor(n * 0.75)].toFixed(2)
  };
}

// ── Teste t pareado ────────────────────────────────────────────────────────
export function testeTpareado(a, b) {
  const pares = a.map((x, i) => [x, b[i]]).filter(([x, y]) =>
    x !== null && y !== null && !isNaN(Number(x)) && !isNaN(Number(y))
  ).map(([x, y]) => [Number(x), Number(y)]);
  if (pares.length < 3) return null;
  const diffs = pares.map(([x, y]) => x - y);
  const n = diffs.length;
  const meanD = diffs.reduce((a, b) => a + b, 0) / n;
  const sdD = Math.sqrt(diffs.reduce((a, b) => a + Math.pow(b - meanD, 2), 0) / (n - 1));
  const t = meanD / (sdD / Math.sqrt(n));
  const df = n - 1;
  // Aproximação do p-valor via distribuição t (two-tailed)
  const p = aproximarP(Math.abs(t), df);
  return { t: +t.toFixed(3), df, p: +p.toFixed(4), n, meanDiff: +meanD.toFixed(2), sdDiff: +sdD.toFixed(2) };
}

// ── Wilcoxon signed-rank (aproximação normal para n>10) ───────────────────
export function testeWilcoxon(a, b) {
  const pares = a.map((x, i) => [x, b[i]]).filter(([x, y]) =>
    x !== null && y !== null && !isNaN(Number(x)) && !isNaN(Number(y))
  ).map(([x, y]) => [Number(x), Number(y)]);
  if (pares.length < 5) return null;
  const diffs = pares.map(([x, y]) => x - y).filter(d => d !== 0);
  const n = diffs.length;
  if (n < 5) return null;
  const absDiffs = diffs.map(d => Math.abs(d));
  const sorted = [...absDiffs].sort((a, b) => a - b);
  const ranks = absDiffs.map(d => {
    const idxs = sorted.reduce((acc, v, i) => v === d ? [...acc, i+1] : acc, []);
    return idxs.reduce((a, b) => a + b, 0) / idxs.length;
  });
  const Wplus = diffs.reduce((sum, d, i) => d > 0 ? sum + ranks[i] : sum, 0);
  const mean = n * (n + 1) / 4;
  const sd = Math.sqrt(n * (n + 1) * (2 * n + 1) / 24);
  const z = (Wplus - mean) / sd;
  const p = 2 * (1 - normalCDF(Math.abs(z)));
  return { W: +Wplus.toFixed(1), z: +z.toFixed(3), p: +p.toFixed(4), n };
}

// ── Qui-quadrado (tabela de contingência 2x2 ou maiores) ─────────────────
export function quiQuadrado(observados) {
  const linhas = observados.length;
  const colunas = observados[0].length;
  const totalLinha = observados.map(row => row.reduce((a, b) => a + b, 0));
  const totalColuna = observados[0].map((_, j) => observados.reduce((a, row) => a + row[j], 0));
  const total = totalLinha.reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  let chi2 = 0;
  for (let i = 0; i < linhas; i++) {
    for (let j = 0; j < colunas; j++) {
      const esperado = (totalLinha[i] * totalColuna[j]) / total;
      if (esperado > 0) chi2 += Math.pow(observados[i][j] - esperado, 2) / esperado;
    }
  }
  const df = (linhas - 1) * (colunas - 1);
  const p = 1 - chiCDF(chi2, df);
  return { chi2: +chi2.toFixed(3), df, p: +p.toFixed(4), total };
}

// ── Distribuições ─────────────────────────────────────────────────────────
function normalCDF(x) {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp(-x * x / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return x > 0 ? 1 - p : p;
}

function aproximarP(t, df) {
  // Aproximação de Cornish-Fisher para distribuição t
  const x = df / (df + t * t);
  let p = incompletaBeta(x, df / 2, 0.5);
  return Math.min(1, Math.max(0, p));
}

function incompletaBeta(x, a, b) {
  // Aproximação via série de Lentz
  if (x < 0 || x > 1) return 0;
  if (x === 0) return 0;
  if (x === 1) return 1;
  const lbeta = lgamma(a + b) - lgamma(a) - lgamma(b);
  const front = Math.exp(Math.log(x) * a + Math.log(1 - x) * b - lbeta) / a;
  return front * betaCF(x, a, b);
}

function betaCF(x, a, b) {
  const MAXIT = 100, EPS = 3e-7;
  let qab = a + b, qap = a + 1, qam = a - 1;
  let c = 1, d = 1 - qab * x / qap;
  if (Math.abs(d) < 1e-30) d = 1e-30;
  d = 1 / d; let h = d;
  for (let m = 1; m <= MAXIT; m++) {
    let m2 = 2 * m;
    let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
    d = 1 + aa * d; if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + aa / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d; h *= d * c;
    aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
    d = 1 + aa * d; if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + aa / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d;
    const del = d * c; h *= del;
    if (Math.abs(del - 1) < EPS) break;
  }
  return h;
}

function lgamma(x) {
  const c = [76.18009172947146,-86.50532032941677,24.01409824083091,
    -1.231739572450155,0.1208650973866179e-2,-0.5395239384953e-5];
  let y = x, tmp = x + 5.5;
  tmp -= (x + 0.5) * Math.log(tmp);
  let ser = 1.000000000190015;
  for (let j = 0; j < 6; j++) ser += c[j] / ++y;
  return -tmp + Math.log(2.5066282746310005 * ser / x);
}

function chiCDF(x, k) {
  if (x <= 0) return 0;
  return gammaInc(k / 2, x / 2);
}

function gammaInc(a, x) {
  if (x < 0) return 0;
  if (x < a + 1) {
    let ap = a, del = 1 / a, sum = del;
    for (let i = 0; i < 100; i++) {
      ap++; del *= x / ap; sum += del;
      if (Math.abs(del) < Math.abs(sum) * 3e-7) break;
    }
    return sum * Math.exp(-x + a * Math.log(x) - lgamma(a));
  }
  let b = x + 1 - a, c = 1e30, d = 1 / b, h = d;
  for (let i = 1; i <= 100; i++) {
    const an = -i * (i - a); b += 2;
    d = an * d + b; if (Math.abs(d) < 1e-30) d = 1e-30;
    c = b + an / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d; const del = d * c; h *= del;
    if (Math.abs(del - 1) < 3e-7) break;
  }
  return 1 - Math.exp(-x + a * Math.log(x) - lgamma(a)) * h;
}

export function testeTIndependente(a, b) {
  const va = a.filter(x => x !== null && x !== undefined && !isNaN(Number(x))).map(Number);
  const vb = b.filter(x => x !== null && x !== undefined && !isNaN(Number(x))).map(Number);
  if (va.length < 2 || vb.length < 2) return null;
  const na = va.length, nb = vb.length;
  const ma = va.reduce((s, x) => s + x, 0) / na;
  const mb = vb.reduce((s, x) => s + x, 0) / nb;
  const s2a = va.reduce((s, x) => s + Math.pow(x - ma, 2), 0) / (na - 1);
  const s2b = vb.reduce((s, x) => s + Math.pow(x - mb, 2), 0) / (nb - 1);
  const se = Math.sqrt(s2a / na + s2b / nb);
  if (se === 0) return null;
  const t = (ma - mb) / se;
  const df = Math.pow(s2a / na + s2b / nb, 2) /
    (Math.pow(s2a / na, 2) / (na - 1) + Math.pow(s2b / nb, 2) / (nb - 1));
  const p = aproximarP(Math.abs(t), df);
  const spooled = Math.sqrt(((na - 1) * s2a + (nb - 1) * s2b) / (na + nb - 2));
  const d = spooled > 0 ? Math.abs(ma - mb) / spooled : 0;
  const dLabel = d < 0.2 ? 'negligível' : d < 0.5 ? 'pequeno' : d < 0.8 ? 'moderado' : 'grande';
  return { t: +t.toFixed(3), df: +df.toFixed(1), p: +p.toFixed(4), na, nb, ma: +ma.toFixed(2), mb: +mb.toFixed(2), d: +d.toFixed(3), dLabel };
}

export function mannWhitneyU(a, b) {
  const va = a.filter(x => x !== null && !isNaN(Number(x))).map(Number);
  const vb = b.filter(x => x !== null && !isNaN(Number(x))).map(Number);
  if (va.length < 3 || vb.length < 3) return null;
  const na = va.length, nb = vb.length;
  let U1 = 0;
  for (const x of va) for (const y of vb) {
    if (x > y) U1++; else if (x === y) U1 += 0.5;
  }
  const U2 = na * nb - U1;
  const U = Math.min(U1, U2);
  const mean = na * nb / 2;
  const sd = Math.sqrt(na * nb * (na + nb + 1) / 12);
  const z = (U - mean) / sd;
  const p = 2 * (1 - normalCDF(Math.abs(z)));
  return { U: +U.toFixed(1), z: +z.toFixed(3), p: +p.toFixed(4), na, nb };
}

export function anovaOneWay(grupos) {
  const dados = grupos.map(g => g.filter(x => x !== null && !isNaN(Number(x))).map(Number));
  if (dados.some(g => g.length < 2)) return null;
  const ns = dados.map(g => g.length);
  const medias = dados.map(g => g.reduce((s, x) => s + x, 0) / g.length);
  const N = ns.reduce((s, n) => s + n, 0);
  const mediaGlobal = dados.flat().reduce((s, x) => s + x, 0) / N;
  const SSB = ns.reduce((s, n, i) => s + n * Math.pow(medias[i] - mediaGlobal, 2), 0);
  const SSW = dados.reduce((s, g, i) => s + g.reduce((ss, x) => ss + Math.pow(x - medias[i], 2), 0), 0);
  const k = dados.length;
  const dfB = k - 1, dfW = N - k;
  if (dfW <= 0 || SSW === 0) return null;
  const MSB = SSB / dfB, MSW = SSW / dfW;
  const F = MSB / MSW;
  const p = 1 - chiCDF(F * dfB, dfB);
  const eta2 = SSB / (SSB + SSW);
  const eta2Label = eta2 < 0.01 ? 'negligível' : eta2 < 0.06 ? 'pequeno' : eta2 < 0.14 ? 'moderado' : 'grande';
  return { F: +F.toFixed(3), dfB, dfW, p: +p.toFixed(4), eta2: +eta2.toFixed(3), eta2Label, medias: medias.map(m => +m.toFixed(2)), ns };
}

export function kruskalWallis(grupos) {
  const dados = grupos.map(g => g.filter(x => x !== null && !isNaN(Number(x))).map(Number));
  if (dados.some(g => g.length < 2)) return null;
  const ns = dados.map(g => g.length);
  const N = ns.reduce((s, n) => s + n, 0);
  const todos = dados.flatMap((g, i) => g.map(v => ({ v, g: i }))).sort((a, b) => a.v - b.v);
  let i = 0;
  while (i < todos.length) {
    let j = i;
    while (j < todos.length && todos[j].v === todos[i].v) j++;
    const rank = (i + j + 1) / 2;
    for (let k = i; k < j; k++) todos[k].rank = rank;
    i = j;
  }
  const rankSums = Array(dados.length).fill(0);
  todos.forEach(({ g, rank }) => { rankSums[g] += rank; });
  const H = (12 / (N * (N + 1))) * rankSums.reduce((s, R, i) => s + R * R / ns[i], 0) - 3 * (N + 1);
  const df = dados.length - 1;
  const p = 1 - chiCDF(H, df);
  return { H: +H.toFixed(3), df, p: +p.toFixed(4), ns };
}

export function pLabel(p) {
  if (p === null || p === undefined) return '—';
  if (p < 0.001) return 'p < 0,001 ***';
  if (p < 0.01)  return `p = ${p.toFixed(3)} **`;
  if (p < 0.05)  return `p = ${p.toFixed(3)} *`;
  return `p = ${p.toFixed(3)} ns`;
}

export function fmtNum(v, dec = 2) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return Number(v).toFixed(dec);
}
